import React, { useEffect, useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../AuthContext';
import { useLang } from '../i18n';
import { getStudySettings } from '../api';
import { useBugReport } from './BugReport';

const AUTH_ITEMS = [
  { to: '/course', key: 'menu.course' },
  { to: '/study', key: 'menu.study' },
  { to: '/progress', key: 'menu.progress' },
  { to: '/topics', key: 'menu.reading', ruOnly: true },  // генератор текстов пишет по-итальянски
  { to: '/dashboard', key: 'menu.account' },
];
const ADMIN_ITEMS = [{ to: '/admin/users', key: 'menu.admin' }];
const GUEST_ITEMS = [
  { to: '/login', key: 'menu.login' },
  { to: '/register', key: 'menu.register' },
];

export default function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated } = useAuth();
  const { lang, setLang, t } = useLang();
  const { openBugReport } = useBugReport();
  const isAdmin = localStorage.getItem('userRole') === 'admin';

  // язык интерфейса вошедшего пользователя хранится в его настройках
  useEffect(() => {
    if (!isAuthenticated) return;
    getStudySettings().then((s) => s?.ui_lang && setLang(s.ui_lang)).catch(() => {});
  }, [isAuthenticated, setLang]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const items = [
    { to: '/donate', key: 'menu.donate' },
    ...(isAuthenticated ? [...AUTH_ITEMS, ...(isAdmin ? ADMIN_ITEMS : [])] : GUEST_ITEMS),
  ]
    .filter((item) => !(item.ruOnly && lang === 'it'))
    .map((item) => ({ ...item, label: t(item.key) }));

  const buttonSx = {
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
    },
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: 'transparent', mt: 2 }}>
        <Container maxWidth={false}>
          <Toolbar
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0.5rem 0',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'center' }}>
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                🧠 Memo
              </Typography>

              {!isMobile && items.map((item) => (
                <Button key={item.to} component={Link} to={item.to} sx={buttonSx}>
                  {item.label}
                </Button>
              ))}
              {!isMobile && (
                <Button onClick={() => openBugReport()} sx={buttonSx}>{t('menu.bug')}</Button>
              )}
            </Box>

            {isMobile && (
              <>
                <IconButton
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMenuOpen}
                  sx={{ color: theme.palette.text.primary, ml: 'auto' }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {[{ to: '/', label: t('menu.home') }, ...items].map((item) => (
                    <MenuItem
                      key={item.to}
                      component={Link}
                      to={item.to}
                      onClick={handleMenuClose}
                      sx={{ '&:hover': { color: theme.palette.primary.main } }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                  <MenuItem
                    onClick={() => { handleMenuClose(); openBugReport(); }}
                    sx={{ '&:hover': { color: theme.palette.primary.main } }}
                  >
                    {t('menu.bug')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }} disableGutters>
        <Outlet />
      </Container>
    </>
  );
}
